// simui-view.ts — the 4 in-game SimUIForm OVERLAY TEXT LABELS as a WEB-FONT DOM
// overlay (HTML/DOM UI shell plan, Phase 5). These are the text annotations the
// canvas SimUIForm paints OVER the live playfield (game/SimUI.cpp; ts: ui/simuiform.ts):
//   - OBJECTIVE banner (kidcObjective 1005) — top-LEFT. The local player's current
//     objective string (plrLocal.GetObjective(), simuiform.ts:494-495). Shown iff
//     non-empty.
//   - COUNTDOWN timer (kidcCountdown 1006) — top-RIGHT, right-aligned. A mission
//     countdown (host UpdateCountdownString → gamesession.ts:1090). Its kfCtlVisible
//     is toggled dynamically by ShowCountdownTimer (gamesession.ts:1085), so the DOM
//     shows it iff the canvas control is visible.
//   - FPS readout (kidcFps 1004) — top-RIGHT, one line BELOW the countdown. The frame
//     rate "whole.fraction" (simuiform.ts:512-514). Shown iff gfShowFPS (deps.fShowFPS).
//   - ALERT toast (kidcAlert 2006) — bottom-LEFT. A transient status message
//     (AlertControl.AddText → ShowAlert, misccontrols.ts:648-669); its kfCtlVisible is
//     raised on AddText and dropped by a 4s timer (OnTimer), so the DOM shows it iff the
//     canvas control is visible.
// All 4 use canvas font index 1 = kifntShadow → here the WEB body face (FONT_BODY,
// Eurostile Extended) per the DOM UI design preference (the canvas bitmap text is
// suppressed in simuiform.ts when domSimUI is active — see Init/the paint-suppression).
//
// LIKE hud-view.ts: this is a pointer-events:none FIXED overlay over the live canvas;
// positions are in GAME px × magnify (kGameScale), NOT the chrome.ts design stage; the
// host pushes a model each frame (SimUIForm.Update) and update() DIFFS vs this.last so it
// is a no-op when nothing changed. DISPLAY-only — no sim/command/wire touch.
//
// POSITIONS mirror SimUIForm.SizeAndPositionEverything (simuiform.ts:411-462), in GAME
// px, with safe-area margins (cxLeftMargin = max(safe.left, 4), cxRightMargin =
// max(safe.right, 4), cyTopMargin = max(safe.top, 4)) × magnify; the overlay is anchored
// to the WINDOW edges (the canvas SimUIForm covers the whole display, so its labels'
// game-px margins map straight to window-edge margins × magnify).

import { el, FONT_BODY, COL_FG, TEXT_SHADOW } from '../chrome.ts';

/** The per-frame model the host pushes (read off the live canvas controls in
 *  SimUIForm.Update, AFTER the string-update arms run so the text is canonical).
 *  Each string already lives on a control — no new sim getters are needed. */
export interface SimUIModel {
  /** kidcObjective.GetText() — the objective banner; shown iff non-empty. */
  objective: string;
  /** kidcAlert: the toast text + its live kfCtlVisible (raised by AddText, dropped by
   *  the 4s timer) — shown iff visible. */
  alert: { text: string; visible: boolean };
  /** kidcCountdown: the timer text + its live kfCtlVisible (toggled by
   *  ShowCountdownTimer) — shown iff visible. */
  countdown: { text: string; visible: boolean };
  /** kidcFps: the "whole.fraction" rate + visible = deps.fShowFPS. */
  fps: { text: string; visible: boolean };
}

export interface SimUIViewDeps {
  /** the overlay container (created by the host; this view owns its contents). It is
   *  the pointer-events:none input-split container — set by this view's ctor. */
  root: HTMLElement;
  /** the canvas magnification (kGameScale — 1.5 desktop / 1.0 small). The canvas draws
   *  the labels' game-px positions at native resolution and presents the whole back
   *  buffer scaled by this; the DOM overlay applies the SAME factor so the labels land
   *  at the IDENTICAL screen position as the canvas SimUIForm text. */
  magnify: number;
  /** gpdisp->GetSafeAreas (SimUI.cpp:363) in GAME px — the same source the canvas
   *  SimUIForm reads (currently the all-zero stub). The margins are max(safe, 4). */
  getSafeAreas?: () => { left: number; top: number; right: number; bottom: number };
  /** the kifntShadow line height in GAME px (off the running game's font). The DOM text
   *  is sized to this × magnify so its line height matches the canvas labels. Absent ⇒
   *  the faithful default (≈ the 7-game-px kifntShadow line). */
  fontPx?: number;
  /** the bottom HUD bar height in GAME px (gs.consoleStripHeight). The canvas SimUIForm's
   *  playfield excludes the InputUI strip, so the bottom-left ALERT toast is pinned ABOVE
   *  the bar — at the lower-left of the MAP area — not over the bar. */
  consoleStripPx?: number;
  /** the DOM HUD BAR's magnification (main.ts kUiMagnify). The bar height = consoleStripPx ×
   *  this, which is LARGER than the alert's own `magnify` on phones; the ALERT toast clears a
   *  bar this tall. Defaults to `magnify` (when the bar matched the map scale). */
  hudMagnify?: number;
}

// SimUIForm.SizeAndPositionEverything (simuiform.ts:425): cxyMargin = 4 game px.
const CXY_MARGIN = 4;
// kifntShadow line height in GAME px (game.ini [Fonts] 1 — the in-game shadow body
// font). Used to gap the FPS one line below the countdown + to size the web text when
// the live font height isn't supplied.
const KIFNT_SHADOW_PX = 7;

export class SimUIView {
  private readonly d: SimUIViewDeps;
  /** the 4 labels — each an absolutely-positioned child of the input-split container. */
  private objectiveEl!: HTMLElement;
  private countdownEl!: HTMLElement;
  private fpsEl!: HTMLElement;
  private alertEl!: HTMLElement;
  /** the last model pushed — update() is a no-op when nothing changed (cheap). */
  private last: SimUIModel | null = null;

  constructor(d: SimUIViewDeps) {
    this.d = d;
    // THE INPUT SPLIT (like hud-view): the container is a full-window overlay at
    // pointer-events:none so every gesture over it (the map) passes THROUGH to the
    // canvas underneath. The labels are display-only text — they never opt back in.
    const s = d.root.style;
    s.position = 'fixed';
    s.inset = '0';
    s.zIndex = '10'; // above the canvas, BELOW the modal overlays (z=20)
    s.pointerEvents = 'none'; // ← the split: map gestures fall through to canvas
    s.userSelect = 'none';
    s.fontFamily = FONT_BODY;
    this.build();
    window.addEventListener('resize', this.onResize);
  }

  private readonly onResize = (): void => {
    this.layout();
  };

  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.d.root.replaceChildren();
    this.last = null;
  }

  /** the canvas magnification (kGameScale) — the same factor the canvas presents its
   *  game-px back buffer at, so the DOM labels land at the identical screen position. */
  private unit(): number {
    return this.d.magnify;
  }

  private build(): void {
    // a label: a fixed-position web-font text element. The objective + alert are
    // LEFT-aligned (top-left / bottom-left anchors); the countdown + fps are
    // RIGHT-aligned (top-right anchors, mirroring the canvas SetPosition that right-pins
    // them to sizSim.cx - width - rightMargin, simuiform.ts:439/445).
    const mkLabel = (align: 'left' | 'right'): HTMLElement => {
      const e = el('div', {
        position: 'absolute',
        color: COL_FG, // kifntShadow ≈ kiclrWhite
        textShadow: TEXT_SHADOW,
        whiteSpace: 'nowrap',
        textAlign: align,
        pointerEvents: 'none',
        display: 'none', // hidden until update() shows it
        lineHeight: '1',
      });
      return e;
    };
    this.objectiveEl = mkLabel('left');
    this.objectiveEl.setAttribute('data-simui', 'objective');
    // the OBJECTIVE banner can be a full tutorial sentence ("Select the Dominion, then select a
    // destination…") — far wider than a phone screen. Let it WRAP onto multiple lines instead of
    // running off the right edge (the width is bounded in layout()); a touch more line-height keeps
    // the wrapped lines legible. The other 3 labels stay nowrap (they're short — a timer / fps /
    // brief toast).
    this.objectiveEl.style.whiteSpace = 'normal';
    this.objectiveEl.style.lineHeight = '1.15';
    this.countdownEl = mkLabel('right');
    this.countdownEl.setAttribute('data-simui', 'countdown');
    this.fpsEl = mkLabel('right');
    this.fpsEl.setAttribute('data-simui', 'fps');
    this.alertEl = mkLabel('left');
    this.alertEl.setAttribute('data-simui', 'alert');

    this.d.root.replaceChildren(this.objectiveEl, this.countdownEl, this.fpsEl, this.alertEl);
    this.layout();
  }

  // position the 4 labels to the canvas geometry × the magnification. The canvas
  // SimUIForm.SizeAndPositionEverything (simuiform.ts:411-462) lays them out in GAME px
  // over a form that covers the whole display; the whole back buffer is then presented
  // scaled by kGameScale — so the DOM overlay applies the SAME factor to the SAME
  // game-px margins, anchored to the window edges, and lands at the IDENTICAL position.
  private layout(): void {
    const u = this.unit();
    const safe = this.d.getSafeAreas?.() ?? { left: 0, top: 0, right: 0, bottom: 0 };
    // simuiform.ts:426-428 — cxLeftMargin/cxRightMargin/cyTopMargin = max(safe, 4).
    const cxLeft = Math.round(Math.max(safe.left, CXY_MARGIN) * u);
    const cxRight = Math.round(Math.max(safe.right, CXY_MARGIN) * u);
    const cyTop = Math.round(Math.max(safe.top, CXY_MARGIN) * u);
    // getSafeAreas is the all-zero stub in the live game (shell/gamesession/hud-seams all pass
    // {0,0,0,0}), so the game-px margins above never clear the iPhone notch / status bar — the
    // objective + countdown labels land UNDER it. Fold the REAL CSS safe-area insets in here so
    // the top labels drop to just below the status bar while the full-bleed map keeps covering it.
    const leftCss = `max(${cxLeft}px, env(safe-area-inset-left, 0px))`;
    const rightCss = `max(${cxRight}px, env(safe-area-inset-right, 0px))`;
    const topCss = `max(${cyTop}px, env(safe-area-inset-top, 0px))`;
    const lineH = Math.round((this.d.fontPx ?? KIFNT_SHADOW_PX) * u);

    // the web text size = the kifntShadow line height × magnification × FONT_SCALE. The
    // Eurostile web face reads noticeably larger than the canvas kifntShadow bitmap at the
    // same nominal height, so scale it down to sit closer to the canvas label size.
    const FONT_SCALE = 0.8;
    const fontPx = Math.max(8, Math.round(lineH * FONT_SCALE));
    const fontSize = `${fontPx}px`;
    for (const e of [this.objectiveEl, this.countdownEl, this.fpsEl, this.alertEl]) {
      e.style.fontSize = fontSize;
    }

    // OBJECTIVE — top-LEFT at (cxLeftMargin, cyTopMargin) (simuiform.ts:432-433). Bound its width
    // to the space between the left and right margins so a long objective WRAPS onto a second line
    // within the screen instead of overflowing off the right edge (whiteSpace:normal set in build).
    this.objectiveEl.style.left = leftCss;
    this.objectiveEl.style.top = topCss;
    this.objectiveEl.style.maxWidth = `calc(100vw - ${leftCss} - ${rightCss})`;

    // COUNTDOWN — top-RIGHT (the canvas right-pins it to sizSim.cx - width -
    // cxRightMargin; right-anchored CSS gives the same right edge, simuiform.ts:437-439).
    this.countdownEl.style.right = rightCss;
    this.countdownEl.style.top = topCss;

    // FPS — top-RIGHT, one LINE below the countdown (the canvas drops it by rcT.Height()
    // = the countdown control's height = one kifntShadow line, simuiform.ts:443-446).
    this.fpsEl.style.right = rightCss;
    // drop the FPS by the ACTUAL rendered line height (fontPx), not the unscaled lineH — the
    // Math.max(8,…) floor can make the text taller than lineH (e.g. magnify 1.0 → lineH 7,
    // fontPx 8), which would overlap the countdown above it.
    this.fpsEl.style.top = `calc(${topCss} + ${fontPx}px)`;

    // ALERT — bottom-LEFT, ABOVE the bottom HUD bar. The canvas pins it to sizSim.cy -
    // height, where sizSim is the PLAYFIELD (excludes the InputUI console strip), so it sits
    // at the lower-left of the MAP area, not over the bar. Anchor its bottom at the bar
    // height (consoleStripPx × magnify) so it clears the DOM HUD bar (simuiform.ts:459-461).
    this.alertEl.style.left = `${cxLeft}px`;
    // clear the DOM HUD bar (height = consoleStripPx × the HUD's magnify, which is LARGER than
    // the alert's own `u` on phones — using `u` here left the toast behind the taller bar), plus
    // a 4 game-px gap (the toast always sat flush against the bar — a touch too low).
    const barPx = (this.d.consoleStripPx ?? 0) * (this.d.hudMagnify ?? u);
    this.alertEl.style.bottom = `${Math.round(barPx + 4 * u)}px`;
  }

  /** the per-frame push from SimUIForm.Update — re-render only on change (cheap).
   *  DISPLAY-only. */
  update(model: SimUIModel): void {
    const p = this.last;
    if (
      p !== null &&
      p.objective === model.objective &&
      p.alert.text === model.alert.text &&
      p.alert.visible === model.alert.visible &&
      p.countdown.text === model.countdown.text &&
      p.countdown.visible === model.countdown.visible &&
      p.fps.text === model.fps.text &&
      p.fps.visible === model.fps.visible
    ) {
      return; // nothing changed
    }
    this.last = {
      objective: model.objective,
      alert: { ...model.alert },
      countdown: { ...model.countdown },
      fps: { ...model.fps },
    };

    // OBJECTIVE — shown iff non-empty (the canvas LabelControl self-sizes to 0 and
    // paints nothing when m_szLabel is null/empty; the DOM hides it).
    this.setLabel(this.objectiveEl, model.objective, model.objective.trim().length > 0);
    // ALERT / COUNTDOWN — shown iff the canvas control's kfCtlVisible is set.
    this.setLabel(this.alertEl, model.alert.text, model.alert.visible);
    this.setLabel(this.countdownEl, model.countdown.text, model.countdown.visible);
    // FPS — shown iff gfShowFPS (model.fps.visible = deps.fShowFPS).
    this.setLabel(this.fpsEl, model.fps.text, model.fps.visible);
  }

  private setLabel(e: HTMLElement, text: string, visible: boolean): void {
    if (visible && text.length > 0) {
      if (e.textContent !== text) e.textContent = text;
      e.style.display = 'block';
    } else {
      e.style.display = 'none';
    }
  }
}
